import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/** Delta de scroll vers le HAUT avant de réafficher le header produit (évite le flicker). */
const SCROLL_SHOW_THRESHOLD = 12;
/** Delta de scroll vers le BAS avant de masquer le header produit : quasi nul → disparition dès le 1er pixel. */
const SCROLL_HIDE_THRESHOLD = 1;
/** Seuil scroll pour activer le fond du header (always) — évite le flip près de 0 sur Chrome iOS. */
const SCROLL_ACTIVE_MIN = 2;

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Bound visualViewport listeners (Chrome iOS)
   * @type {(() => void) | null}
   */
  #viewportSyncHandler = null;

  /**
   * @returns {boolean}
   */
  #isMobileTouch = () => window.matchMedia('(max-width: 749px)').matches;

  /**
   * Position de scroll fiable (Chrome iOS : visualViewport + layout viewport).
   * @returns {number}
   */
  #getScrollTop = () => {
    const layoutScroll = document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
    const viewport = window.visualViewport;

    if (!viewport || !this.#isMobileTouch()) return layoutScroll;

    return Math.max(layoutScroll, viewport.pageTop ?? 0);
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        const scrollTop = this.#getScrollTop();
        this.dataset.stickyState = isIntersecting || scrollTop <= 0 ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * @param {boolean} enable
   */
  #bindViewportSync = (enable) => {
    const viewport = window.visualViewport;
    if (!viewport || !this.#isMobileTouch()) return;

    if (enable) {
      if (this.#viewportSyncHandler) return;
      this.#viewportSyncHandler = () => this.#handleWindowScroll();
      viewport.addEventListener('scroll', this.#viewportSyncHandler, { passive: true });
      viewport.addEventListener('resize', this.#viewportSyncHandler, { passive: true });
      return;
    }

    if (!this.#viewportSyncHandler) return;
    viewport.removeEventListener('scroll', this.#viewportSyncHandler);
    viewport.removeEventListener('resize', this.#viewportSyncHandler);
    this.#viewportSyncHandler = null;
  };

  /**
   * @param {'inactive' | 'active' | 'idle'} state
   */
  #setStickyState = (state) => {
    if (this.dataset.stickyState === state) return;
    this.dataset.stickyState = state;
    if (state === 'active' && this.dataset.themeColor) {
      changeMetaThemeColor(this.dataset.themeColor);
    }
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    const scrollTop = this.#getScrollTop();

    if (scrollTop < SCROLL_ACTIVE_MIN) {
      this.#offscreen = false;
      this.#setStickyState('inactive');
      this.dataset.scrollDirection = 'none';
      this.#lastScrollTop = 0;
      return;
    }

    const scrollDelta = scrollTop - this.#lastScrollTop;

    if (stickyMode === 'scroll-up') {
      if (scrollDelta < 0) {
        // Remonte : seuil pour éviter que le header flickère sur un micro-geste.
        if (-scrollDelta < SCROLL_SHOW_THRESHOLD) return;
        this.#setStickyState('active');
        this.dataset.scrollDirection = 'up';
      } else {
        // Descend : disparition immédiate, dès le tout premier pixel de scroll.
        if (scrollDelta < SCROLL_HIDE_THRESHOLD) return;
        this.#setStickyState('idle');
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (stickyMode === 'always') {
      this.#setStickyState('active');

      if (scrollDelta < 0) {
        this.dataset.scrollDirection = 'up';
      } else if (scrollDelta > 0) {
        this.dataset.scrollDirection = 'down';
      } else {
        this.dataset.scrollDirection = 'none';
      }

      this.#lastScrollTop = scrollTop;
    }
  };

  connectedCallback() {
    super.connectedCallback();
    const resizeTarget =
      this.#isMobileTouch() && this.refs.headerRowTop ? this.refs.headerRowTop : this;
    this.#resizeObserver.observe(resizeTarget);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }

      if (stickyMode === 'always') {
        // Pas d'IntersectionObserver : avec Chrome iOS la barre d'adresse fait
        // osciller isIntersecting ↔ scrollTop et provoque tremblement + crop.
        this.#bindViewportSync(true);
        this.#updateScrollState();
      } else if (stickyMode === 'scroll-up') {
        this.#observeStickyPosition(false);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#bindViewportSync(false);
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
